import { Suspense } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { log } from "@quest-web/shared-module";
import { useOnlineStatus } from "@leagueplatform/web-common";
import { LoadingIndicator } from "@leagueplatform/web-common-components";
import { LeagueProvider } from "@leagueplatform/core";
import { ObservabilityErrorBoundary } from "@leagueplatform/observability";
import { initMasonryRegistry } from "utils/init-masonry-registry";
import { config } from "config";
import { appRoutes } from "app-routes";
import { GenericError } from "components/generic-error/generic-error";
import { LazyQuestSchedulingFlowBridge } from "components/lazy-routes.component";
import { SchedulingFlowOverlayHost } from "components/scheduling-flow-overlay-host/scheduling-flow-overlay-host.component";
import { careDocumentsModalOverrides } from "theme/care-documents-modal-overrides";
import { pageStyleOverrides } from "theme/page-style-overrides";
import { activityViewerOverrides } from "theme/activity-viewer-overrides";

initMasonryRegistry();

log("test message");

const LeagueProviderWrapper = () => {
  const { isOffline, isOnline } = useOnlineStatus();
  careDocumentsModalOverrides();
  pageStyleOverrides();
  activityViewerOverrides();
  return (
    <LeagueProvider config={config}>
      <ObservabilityErrorBoundary
        fallback={<GenericError />}
        errorContext={{
          context: { Context: { location: "Root App ErrorBoundary" } },
        }}
      >
        <Suspense fallback={<LoadingIndicator />}>
          {isOffline && <GenericError />}
          {isOnline && <Outlet />}
        </Suspense>
        {/* QUEST-2796 — mount the scheduling-flow bridge and overlay globally
            so `startSchedulingFlow` works on any route, not just Screenings.
            Isolated Suspense with `fallback={null}` keeps the chunk download
            from blocking the main <Outlet /> route render. */}
        <Suspense fallback={null}>
          <LazyQuestSchedulingFlowBridge />
          <SchedulingFlowOverlayHost />
        </Suspense>
      </ObservabilityErrorBoundary>
      <ReactQueryDevtools initialIsOpen={false} />
    </LeagueProvider>
  );
};

const router = createBrowserRouter([
  {
    element: <LeagueProviderWrapper />,
    children: appRoutes,
  },
]);

export const App = () => <RouterProvider router={router} />;
